import { Injectable } from '@angular/core';
import { BehaviorSubject, defer, EMPTY, merge, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, pairwise, switchMap, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { ILogger } from '@nimbus-library/features/logger/services/logger/i-logger';
import { NimbusSignalRHub } from '@nimbus-global/features/signalr/nimbusSignalR.hub';
import { IUserResponsibilityProfile } from '../models/user-responsibility-profile.model';
import { UserStateApiClient } from './api/user-state.api-client';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class ResponsibilityProfileService {
  private initialized = false;
  private readonly reload$ = new Subject<void>();

  public readonly profiles$ = new BehaviorSubject<IUserResponsibilityProfile[]>([]);
  public readonly isActivating$ = new BehaviorSubject<boolean>(false);
  public readonly activeProfile$: Observable<IUserResponsibilityProfile | null> = this.profiles$.pipe(
    map((profiles) => profiles.find((profile) => profile.active) ?? null),
  );

  public constructor(
    private readonly apiClient: UserStateApiClient,
    private readonly authenticationService: IAuthenticationService,
    private readonly logger: ILogger,
    private readonly signalRHub: NimbusSignalRHub,
  ) {}

  public initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    // A single switchMapped pipeline owns every load so an identity change (or a subsequent reload)
    // cancels the previous user's in-flight request — a late response can no longer repopulate
    // profiles$ after reset() or overwrite the new user's profiles.
    this.authenticationService.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        switchMap((isAuthenticated) =>
          isAuthenticated ? this.reloadTriggers$().pipe(switchMap(() => this.loadProfiles())) : this.clear(),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  public reset(): void {
    this.profiles$.next([]);
    this.isActivating$.next(false);
  }

  public refresh(): void {
    this.reload$.next();
  }

  public activateProfile(responsibilityProfileId: string): Observable<void> {
    // defer() keeps all side effects (saving flag, logging, HTTP) lazy so they only run on subscription.
    return defer(() => {
      const { userId } = this.authenticationService.getUserInfo();
      this.isActivating$.next(true);
      return this.apiClient.activateUserResponsibilityProfile(userId, responsibilityProfileId).pipe(
        tap(() => this.refresh()),
        finalize(() => this.isActivating$.next(false)),
        catchError((error) => {
          this.logger.exception(error, { message: 'Failed to activate user responsibility profile', userId, responsibilityProfileId });
          return EMPTY;
        }),
      );
    });
  }

  private reloadTriggers$(): Observable<unknown> {
    return merge(of(undefined), this.reload$, this.signalRHub.onUserResponsibilityProfilesChanged$, this.reconnected$());
  }

  // Changes made while the hub was disconnected are missed, so reload on every reconnect (not the initial connect).
  private reconnected$(): Observable<[boolean, boolean]> {
    return this.signalRHub.isConnected$.pipe(
      distinctUntilChanged(),
      pairwise(),
      filter(([wasConnected, isConnected]) => wasConnected === false && isConnected === true),
    );
  }

  private clear(): Observable<never> {
    this.reset();
    return EMPTY;
  }

  private loadProfiles(): Observable<IUserResponsibilityProfile[]> {
    const { userId } = this.authenticationService.getUserInfo();
    return this.apiClient.getUserResponsibilityProfiles(userId).pipe(
      tap((profiles) => this.profiles$.next(profiles)),
      catchError((error) => {
        this.logger.exception(error, { message: 'Failed to load user responsibility profiles', userId });
        return EMPTY;
      }),
    );
  }
}
